'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTabPoll } from '../autoRefresh'
import { ChevronDown, ChevronRight, Loader2, Settings2, ShieldCheck } from 'lucide-react'
import { BaseDashboard, baseDashboardClasses } from '../BaseDashboard'
import { ResizableTable, TableTd, type ColumnDef } from '../ResizableTable'
import { StatTile } from '../charts/StatTile'
import { EmailPillInput } from '../EmailPillInput'
import { apiGet, apiPost, useCatalog } from './entriesApi'
import { Chip } from './autoDomainChips'
import { SearchableSelect } from './SearchableSelect'
import { ActionsWidget } from '../EntryDetail/ActionsWidget'
import { type ReportChannel, type EntryActionFlat, type ReportTaskStatus, type ProviderContact, type FormCheck, type FormStatus, TASK_STATUS_LABEL, TASK_STATUS_TONE, MANUAL_CHANNELS, isManualChannel, CAPTCHA_KINDS, captchaLabel, FORM_STATUS_LABEL, FORM_STATUS_TONE, isUnmappedProvider, providerKey } from './actionTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { parseDbTimestamp } from '@/lib/dates'

// Administration › Actions — every action taken across brands (entry_action,
// fraud-dbs 20260915000001; jupyter-fraud libs/tooling/mitigation): the
// planned takedown reports and the manual sends an operator fired from their
// own screen (the SIBS alert, the SIBS case report, a composed email, a
// domain marked inactive). Plus the two things an operator configures here:
// per-brand channel settings (the CNCS address, whether a channel is on, the
// reporter identity the forms use) and the registrar abuse contacts the
// WHOIS does not give. Editor only.
// A row expands into the entry's own Actions widget, so every button an
// analyst has on the entry is here too.

type Summary = { planned: number; queued: number; awaiting_browser: number; sent: number; unanswered_48h: number; takedown_hints: number; failed: number; resolved_7d: number; manual_sends_7d: number }
type ActionList = { rows: EntryActionFlat[]; total: number; page: number; pageSize: number }
type ChannelConfigRow = { id: string; platform_id: number | null; channel: string; enabled: boolean; recipients: string[]; params: Record<string, any>; updated_by: string | null; updated_at: string }
type RegistrarRow = ProviderContact
type ConfigPayload = { channels: ChannelConfigRow[]; registrars: ProviderContact[]; hosters: ProviderContact[]; form_checks: FormCheck[]; catalog: ReportChannel[] }

const STATUS_ORDER: ReportTaskStatus[] = ['planned', 'queued', 'sending', 'awaiting_browser', 'sent', 'acknowledged', 'failed', 'resolved', 'skipped', 'cancelled']
const PLANNED_CHANNELS = ['registrar_abuse', 'registrar_form', 'hoster_abuse', 'hoster_form', 'cncs', 'google_safe_browsing', 'cloudflare_abuse', 'microsoft_report', 'generic_form']
// Workers whose target lives in the provider tables, never in a brand's
// params: per brand only on/off is editable. The CNCS address and the
// reporter identity are global too (one row, platform NULL).
const PROVIDER_CHANNELS = ['registrar_abuse', 'registrar_form', 'hoster_abuse', 'hoster_form']
const GLOBAL_ONLY_CHANNELS = ['cncs', 'reporter']

function fmtDate(iso?: string | null) {
  const d = parseDbTimestamp(iso ?? undefined)
  if (!d) return '—'
  const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
  const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit' }).format(d)
  return `${time} ${date}`
}

function fmtHours(h?: number | null) {
  if (h == null) return '—'
  if (h < 1) return `${Math.round(h * 60)}m`
  if (h < 48) return `${Math.round(h)}h`
  return `${Math.round(h / 24)}d`
}

// What an operator should do next with a row, in one phrase.
function nextAction(t: EntryActionFlat): string {
  switch (t.status) {
    case 'planned': return 'press Report'
    case 'queued': return 'not sent — Retry or the backstop'
    case 'sending': return 'sending now'
    case 'awaiting_browser': return t.handoff_claimed_by ? `browser pane ${t.handoff_claimed_by} on it` : 'needs the browser pane'
    case 'sent': return (t.hours_since_sent ?? 0) > 48 ? 'no reply in 48 h — chase or resolve' : 'waiting for a reply'
    case 'acknowledged': return t.resolution_candidate ? 'reply says taken down — confirm and resolve' : 'read the reply, resolve'
    case 'failed': return `retry (${t.last_error?.split('\n')[0] ?? 'failed'})`
    case 'resolved': return t.resolution ?? 'resolved'
    case 'skipped': return t.applicability?.reason ?? 'not applicable'
    default: return '—'
  }
}

export function ActionsView({ theme }: { theme: ProcessedTheme }) {
  const { isDark, muted, border, panelBg, text } = baseDashboardClasses(theme)
  const { catalog } = useCatalog()
  const platformName = useCallback((id: number | null) => (id == null ? null : catalog.platforms.find((p) => p.id === id)?.name_code ?? `#${id}`), [catalog])

  const [summary, setSummary] = useState<Summary | null>(null)
  const [list, setList] = useState<ActionList | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [statuses, setStatuses] = useState<ReportTaskStatus[]>([])
  const [channels, setChannels] = useState<string[]>([])
  const [platformId, setPlatformId] = useState<number | ''>('')
  const [q, setQ] = useState('')
  const [openOnly, setOpenOnly] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return new URLSearchParams(window.location.search).get('action')
  })
  const [showConfig, setShowConfig] = useState(false)
  const [config, setConfig] = useState<ConfigPayload | null>(null)
  const [configNote, setConfigNote] = useState<string | null>(null)

  const fetchList = useCallback(async (page = 0) => {
    const p = new URLSearchParams({ page: String(page) })
    if (statuses.length) p.set('status', statuses.join(','))
    else if (openOnly) p.set('open', '1')
    if (channels.length) p.set('channel', channels.join(','))
    if (platformId !== '') p.set('platform_ids', String(platformId))
    if (q.trim()) p.set('q', q.trim())
    const r = await apiGet<ActionList>(`/actions/list?${p}`)
    if (r.success && r.data) { setList(page === 0 ? r.data : { ...r.data, rows: [...(list?.rows ?? []), ...r.data.rows] }); setError(null) }
    else setError(r.error ?? 'Could not load the actions')
  }, [statuses, channels, platformId, q, openOnly, list?.rows])

  const fetchSummary = useCallback(async () => {
    const r = await apiGet<Summary>('/actions/summary')
    if (r.success && r.data) setSummary(r.data)
  }, [])

  const refreshAll = useCallback(async () => {
    setRefreshing(true)
    await Promise.all([fetchSummary(), fetchList(0)])
    setRefreshing(false); setLoading(false); setLastUpdated(Date.now())
  }, [fetchSummary, fetchList])

  useEffect(() => { setLoading(true); refreshAll() }, [statuses, channels, platformId, openOnly]) // eslint-disable-line react-hooks/exhaustive-deps
  useTabPoll(`actions_${platformId}_${openOnly}_${statuses}_${channels}`, 30000, () => { fetchSummary(); fetchList(0) })

  const loadConfig = useCallback(async () => {
    const r = await apiGet<ConfigPayload>('/actions/config')
    if (r.success && r.data) setConfig(r.data)
    else setConfigNote(r.error ?? 'Could not load the configuration')
  }, [])
  useEffect(() => { if (showConfig && !config) loadConfig() }, [showConfig, config, loadConfig])

  const saveChannel = async (row: { platform_id: number | null; channel: string; enabled?: boolean; recipients?: string[]; params?: Record<string, any> }) => {
    const r = await apiPost('/actions/config', row)
    setConfigNote(r.success ? 'Saved — the next plan uses it.' : (r.error ?? 'Could not save'))
    if (r.success) loadConfig()
  }
  const saveRegistrar = async (row: Partial<RegistrarRow>) => {
    const r = await apiPost('/actions/registrars', row)
    setConfigNote(r.success ? 'Saved.' : (r.error ?? 'Could not save'))
    if (r.success) loadConfig()
  }
  const deleteRegistrar = async (row: RegistrarRow) => {
    const r = await apiPost(`/actions/registrars/${row.id}/delete`, {})
    setConfigNote(r.success ? 'Deleted.' : (r.error ?? 'Could not delete'))
    if (r.success) loadConfig()
  }
  const saveHoster = async (row: Partial<ProviderContact>) => {
    const r = await apiPost('/actions/hosters', row)
    setConfigNote(r.success ? 'Saved.' : (r.error ?? 'Could not save'))
    if (r.success) loadConfig()
  }
  const deleteHoster = async (row: ProviderContact) => {
    const r = await apiPost(`/actions/hosters/${row.id}/delete`, {})
    setConfigNote(r.success ? 'Deleted.' : (r.error ?? 'Could not delete'))
    if (r.success) loadConfig()
  }

  const tile = (label: string, value: number | undefined, tone: 'red' | 'amber' | 'sky' | 'green') => (
    <div className={`col-span-1 overflow-hidden border ${border}`}>
      <StatTile label={label} value={value == null ? '—' : String(value)} tone={tone} isDark={isDark} />
    </div>
  )

  const columns: ColumnDef[] = [
    { key: 'x', label: '', initialWidth: 28 },
    { key: 'brand', label: 'Brand', initialWidth: 80 },
    { key: 'hostname', label: 'Domain', initialWidth: 220 },
    { key: 'channel', label: 'Channel', initialWidth: 150 },
    { key: 'status', label: 'Status', initialWidth: 120 },
    { key: 'target', label: 'Target', initialWidth: 220 },
    { key: 'age', label: 'Age', initialWidth: 60 },
    { key: 'sent', label: 'Sent', initialWidth: 100 },
    { key: 'reply', label: 'Last reply', initialWidth: 260 },
    { key: 'next', label: 'Next', initialWidth: 260 },
  ]
  // The chips read in two groups: what the planner files, and what an
  // operator did somewhere else and the platform only logged.
  const catalogChannels = useMemo(() => config?.catalog ?? [], [config])
  const plannedChannelIds = useMemo(() => (catalogChannels.length ? catalogChannels.filter((c) => !c.manual_only).map((c) => c.id) : PLANNED_CHANNELS), [catalogChannels])
  const manualChannelIds = useMemo(() => (catalogChannels.length ? catalogChannels.filter((c) => c.manual_only).map((c) => c.id) : [...MANUAL_CHANNELS, 'custom_email']), [catalogChannels])
  const rows = list?.rows ?? []
  const hasMore = list ? (list.page + 1) * list.pageSize < list.total : false

  const channelChip = (ch: string) => {
    const active = channels.includes(ch)
    return (
      <button key={ch} type="button" onClick={() => setChannels((prev) => (prev.includes(ch) ? prev.filter((x) => x !== ch) : [...prev, ch]))}
        className={`rounded-[4px] border px-2.5 py-0.5 font-mono text-[10px] tracking-widest transition-colors ${active ? 'border-success bg-success-surface text-success' : `${border} ${muted} hover:text-success hover:border-success`}`}>
        {ch}
      </button>
    )
  }

  return (
    <BaseDashboard
      theme={theme}
      eyebrow="Operations · Actions"
      title="Actions taken"
      subtitle="Every action taken across brands — the takedown reports the planner files and the manual sends an operator fired — and the channels' configuration."
      infoContent={
        <>
          <p>When a domain is promoted (by the analysis or by hand) the planner records, per channel, whether a report applies and to whom — the registrar's abuse desk from WHOIS, the brand's CNCS address, the browser forms. <strong>Nothing is sent by itself.</strong> Report sends the email in the same request (through Resend, tagged so the reply lands on the row); a browser form is parked for the laptop's Chrome pane, which marks the row sent the moment it answers.</p>
          <p className="mt-1.5">The manual channels — the SIBS alert, the SIBS case report, a composed email, a domain marked inactive — are never planned: each is logged when an operator sends it from the screen that owns it, and appears here as the record of what was done.</p>
          <p className="mt-1.5">Replies to an emailed report come back to its row (the <span className="font-mono">[REF-…]</span> tag), flagged when they read like a takedown. Resolving is always a person's call.</p>
        </>
      }
      onRefresh={refreshAll}
      refreshing={refreshing}
      lastUpdated={lastUpdated}
      storageKey="actions-header"
      widgets={
        <>
          {tile('Ready to send', summary?.planned, 'sky')}
          {tile('Sending · queued', summary?.queued, 'amber')}
          {tile('Awaiting browser', summary?.awaiting_browser, 'amber')}
          {tile('Sent · no reply 48h', summary?.unanswered_48h, 'amber')}
          {tile('Reply says taken down', summary?.takedown_hints, 'green')}
          {tile('Failed', summary?.failed, 'red')}
          {tile('Resolved · 7d', summary?.resolved_7d, 'green')}
          {tile('Manual sends · 7d', summary?.manual_sends_7d, 'sky')}
        </>
      }
      widgetCols={8}
      widgetRows="5.25rem"
    >
      {error && <div className={`mx-4 mt-3 rounded border px-3 py-2 font-mono text-[11px] border-destructive text-destructive`}>{error}</div>}

      {/* ── Filters ─────────────────────────────────────────────── */}
      <div className={`flex flex-wrap items-center gap-2 px-4 py-2 border-b ${border} ${panelBg}`}>
        <button type="button" onClick={() => { setOpenOnly((v) => !v); setStatuses([]) }}
          className={`rounded-[4px] border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${openOnly && statuses.length === 0 ? 'border-signal bg-signal-surface text-signal' : `${border} ${muted}`}`}>open only</button>
        {STATUS_ORDER.map((s) => {
          const active = statuses.includes(s)
          return (
            <button key={s} type="button" onClick={() => setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))}
              className={`rounded-[4px] border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${active ? 'border-signal bg-signal-surface text-signal' : `${border} ${muted} hover:text-signal hover:border-signal`}`}>
              {TASK_STATUS_LABEL[s]}
            </button>
          )
        })}
        <span className={`mx-1 ${muted}`}>·</span>
        <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>planned</span>
        {plannedChannelIds.map(channelChip)}
        <span className={`ml-1 font-mono text-[9px] uppercase tracking-widest ${muted}`}>manual</span>
        {manualChannelIds.map(channelChip)}
        <div className="w-48">
          <SearchableSelect theme={theme} value={platformId === '' ? undefined : platformId} options={catalog.platforms}
            placeholder="All brands" searchPlaceholder="Search platforms…" noneLabel="All brands"
            onChange={(id) => setPlatformId(id ? Number(id) : '')} />
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') refreshAll() }} placeholder="domain…"
          className={`w-40 rounded border px-2 py-0.5 font-mono text-[10px] ${border} bg-card text-foreground`} />
        <span className={`ml-auto font-mono text-[10px] uppercase tracking-widest ${muted}`}>{rows.length} / {list?.total ?? '—'}</span>
        <button type="button" onClick={() => setShowConfig((v) => !v)} className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${showConfig ? 'border-signal text-signal' : `${border} ${muted}`} hover:text-signal hover:border-signal`}>
          <Settings2 className="h-3 w-3" /> Channels
        </button>
      </div>

      {/* ── Channel configuration ───────────────────────────────── */}
      {showConfig && (
        <div className={`px-4 py-3 border-b ${border} ${panelBg}`}>
          <div className={`mb-2 flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest ${muted}`}><Settings2 className="h-3 w-3" /> Channel configuration {configNote && <span className={configNote.startsWith('Saved') || configNote === 'Deleted.' ? 'text-success' : 'text-destructive'}>· {configNote}</span>}</div>
          {!config ? (
            <div className={`font-mono text-[11px] ${muted}`}><Loader2 className="inline h-3 w-3 animate-spin" /> Loading…</div>
          ) : (
            <ChannelConfigPanel theme={theme} config={config} platforms={catalog.platforms} onSaveChannel={saveChannel} onSaveRegistrar={saveRegistrar} onDeleteRegistrar={deleteRegistrar} onSaveHoster={saveHoster} onDeleteHoster={deleteHoster} />
          )}
        </div>
      )}

      {/* ── Actions ─────────────────────────────────────────────── */}
      {/* A plain block, not overflow-auto: a scroll container here (even one that
          never overflows) captured the sticky headers and sat them over the
          newest rows (Jobs page bug, 2026-09-20). The ten-row box and its
          scrolling belong to ResizableTable's maxRows. */}
      <div>
        {loading ? (
          <div className={`flex h-48 items-center justify-center gap-2 font-mono text-[11px] ${muted}`}><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <ResizableTable columns={columns} stickyHeader maxRows={10} theme={theme} isEmpty={rows.length === 0} emptyMessage="No actions match these filters">
            {rows.map((t) => {
              const isOpen = expanded === t.action_id
              return [
                <tr key={t.action_id} data-action-id={t.action_id} onClick={() => setExpanded(isOpen ? null : t.action_id)} className={`cursor-pointer border-b transition-colors border-border hover:bg-secondary`}>
                  <TableTd className={muted}>{isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}</TableTd>
                  <TableTd className={`font-mono ${muted}`}>{t.platform_name_code ?? platformName(t.platform_id) ?? '—'}</TableTd>
                  <TableTd className={`font-mono ${text}`} title={t.subject_label ?? undefined}>{t.subject_label ?? '—'}</TableTd>
                  <TableTd className={`font-mono ${muted}`}>{t.channel}</TableTd>
                  <TableTd><span className="inline-flex items-center gap-1"><Chip label={TASK_STATUS_LABEL[t.status]} tone={TASK_STATUS_TONE[t.status]} />{t.resolution_candidate && t.status !== 'resolved' && <Chip label="taken down?" tone="amber" />}</span></TableTd>
                  <TableTd className={`font-mono truncate ${muted}`} title={t.target ?? undefined}>{t.target ?? t.email_to ?? '—'}</TableTd>
                  <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtHours(t.age_hours)}</TableTd>
                  <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtDate(t.sent_at)}</TableTd>
                  <TableTd className={`font-mono truncate ${muted}`} title={t.last_reply_excerpt ?? undefined}>{t.last_reply_from ? `${t.last_reply_from}: ${t.last_reply_excerpt ?? ''}` : '—'}</TableTd>
                  <TableTd className={`font-mono truncate text-[11px] ${t.status === 'failed' ? ('text-destructive') : muted}`} title={nextAction(t)}>{nextAction(t)}</TableTd>
                </tr>,
                isOpen ? (
                  <tr key={`${t.action_id}-detail`} className={`border-b border-border`}>
                    <td colSpan={columns.length} className="px-4 py-3">
                      {/* A legacy log row (an old manual send) can predate the
                          entry it was about; there is no entry widget to show. */}
                      {t.user_entry_id ? (
                        <ActionsWidget userEntryId={t.user_entry_id} hostname={t.hostname} hasDomain={t.hostname != null} theme={theme} onChanged={() => { fetchSummary(); fetchList(0) }} />
                      ) : (
                        <span className={`font-mono text-[11px] ${muted}`}>not linked to an entry (legacy log)</span>
                      )}
                    </td>
                  </tr>
                ) : null,
              ]
            })}
          </ResizableTable>
        )}
        {hasMore && !loading && (
          <div className="flex justify-center py-3">
            <button type="button" onClick={() => fetchList((list?.page ?? 0) + 1)} className={`rounded border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-signal hover:border-signal`}>Load more</button>
          </div>
        )}
      </div>
    </BaseDashboard>
  )
}

// Channel settings and the providers. Per brand × channel: on/off; for the
// fixed forms, recipients and params. Provider knowledge is universal: the
// registrar/hoster workers take their targets from the provider tables, so a
// brand can only switch them off; the CNCS address and the reporter identity
// are edited on the global row only (one address for every brand). Below the
// channels, "Providers → workers": every registrar and hosting network seen
// in the data (notebook 80 keeps the inventory) with the worker that reaches
// it -- address, form, worker module, how far it is trusted -- and the rows
// that still have none.
function ChannelConfigPanel({ theme, config, platforms, onSaveChannel, onSaveRegistrar, onDeleteRegistrar, onSaveHoster, onDeleteHoster }: {
  theme: ProcessedTheme
  config: ConfigPayload
  platforms: Array<{ id: number; name_code: string; name?: string }>
  onSaveChannel: (row: { platform_id: number | null; channel: string; enabled?: boolean; recipients?: string[]; params?: Record<string, any> }) => Promise<void>
  onSaveRegistrar: (row: Partial<ProviderContact>) => Promise<void>
  onDeleteRegistrar: (row: ProviderContact) => Promise<void>
  onSaveHoster: (row: Partial<ProviderContact>) => Promise<void>
  onDeleteHoster: (row: ProviderContact) => Promise<void>
}) {
  const { muted, border, text } = baseDashboardClasses(theme)
  const [platformId, setPlatformId] = useState<number | null>(null)
  const [drafts, setDrafts] = useState<Record<string, { recipients: string[]; params: string }>>({})
  const inputCls = `rounded border px-2 py-1 font-mono text-[11px] ${border} bg-card text-foreground`

  const rowFor = (channel: string) => config.channels.find((r) => r.channel === channel && (platformId == null ? r.platform_id == null : r.platform_id === platformId))
  const globalFor = (channel: string) => config.channels.find((r) => r.channel === channel && r.platform_id == null)
  // the reporter identity is not a worker, so it is not in the catalog; it
  // is edited here as one more global row
  const reporterRow = globalFor('reporter')
  const reporterDraft = drafts.reporter ?? { recipients: [], params: JSON.stringify(reporterRow?.params ?? {}, null, 0) }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>Brand</span>
        <div className="w-56">
          <SearchableSelect theme={theme} value={platformId ?? undefined} options={platforms.map((p) => ({ id: p.id, name: p.name ?? p.name_code }))}
            placeholder="Global defaults" searchPlaceholder="Search platforms…" noneLabel="Global defaults"
            onChange={(id) => { setPlatformId(id ? Number(id) : null); setDrafts({}) }} />
        </div>
        <span className={`font-mono text-[10px] ${muted}`}>A brand row switches a worker on or off; the CNCS address, the reporter identity and the providers are global.</span>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {config.catalog.map((ch) => {
          const row = rowFor(ch.id)
          const inherited = !row && platformId != null ? globalFor(ch.id) : null
          const eff = row ?? inherited
          const draft = drafts[ch.id] ?? { recipients: eff?.recipients ?? [], params: JSON.stringify(eff?.params ?? {}, null, 0) }
          const setDraft = (d: Partial<typeof draft>) => setDrafts((prev) => ({ ...prev, [ch.id]: { ...draft, ...d } }))
          const manual = ch.manual_only || isManualChannel(ch.id)
          const provider = PROVIDER_CHANNELS.includes(ch.id)
          const globalOnly = GLOBAL_ONLY_CHANNELS.includes(ch.id) && platformId != null
          return (
            <div key={ch.id} className={`rounded border px-3 py-2 ${border}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`font-mono text-[12px] ${text}`}>{ch.label}</span>
                <Chip label={ch.kind} tone="neutral" />
                {ch.role && ch.role !== 'manual' && <Chip label={ch.role} tone="neutral" title="Whose desk this worker reaches" />}
                {manual && <Chip label="manual" tone="neutral" title="Logged when an operator sends it; never planned" />}
                {inherited && <Chip label="inherited" tone="neutral" title="No brand row; the global default applies" />}
                <span className="flex-1" />
                <label className={`inline-flex items-center gap-1 font-mono text-[10px] ${muted}`}>
                  <input type="checkbox" checked={eff?.enabled ?? true} onChange={(e) => onSaveChannel({ platform_id: platformId, channel: ch.id, enabled: e.target.checked })} /> enabled
                </label>
              </div>
              {manual ? (
                <div className={`mt-2 font-mono text-[10px] ${muted}`}>sent from its own screen</div>
              ) : provider ? (
                <div className={`mt-2 font-mono text-[10px] ${muted}`}>target from the provider tables below (the {ch.role}&apos;s abuse desk or form); per brand only on/off</div>
              ) : globalOnly ? (
                <div className={`mt-2 font-mono text-[10px] ${muted}`}>edited on the global row (pick &quot;Global defaults&quot;); per brand only on/off</div>
              ) : (
                <>
                  {ch.kind === 'email' && (
                    <div className="mt-2">
                      <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Recipients</span>
                      <EmailPillInput value={draft.recipients} onChange={(v) => setDraft({ recipients: v })} theme={theme} placeholder={ch.id === 'cncs' ? 'the CNCS / cybersecurity office address (one for every brand)' : 'address'} />
                    </div>
                  )}
                  <div className="mt-2">
                    <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Params (JSON){ch.kind === 'browser_form' ? ' · form_url' : ''}</span>
                    <input value={draft.params} onChange={(e) => setDraft({ params: e.target.value })} className={`${inputCls} mt-0.5 w-full`} />
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <button type="button" onClick={() => {
                      let params: Record<string, any> = {}
                      try { params = JSON.parse(draft.params || '{}') } catch { return }
                      onSaveChannel({ platform_id: platformId, channel: ch.id, recipients: draft.recipients, params })
                    }} className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border-success/40 text-success hover:bg-success-surface`}>Save</button>
                    {eff?.updated_by && <span className={`font-mono text-[10px] ${muted}`}>by {eff.updated_by} · {fmtDate(eff.updated_at)}</span>}
                  </div>
                </>
              )}
            </div>
          )
        })}
        {platformId == null && (
          <div className={`rounded border px-3 py-2 ${border}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`font-mono text-[12px] ${text}`}>Reporter identity</span>
              <Chip label="global" tone="neutral" title="Who we are on every provider form; never per brand" />
            </div>
            <div className="mt-2">
              <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Params (JSON) · reporter_name, reporter_email, company, country, phone, relationship</span>
              <input value={reporterDraft.params} onChange={(e) => setDrafts((prev) => ({ ...prev, reporter: { ...reporterDraft, params: e.target.value } }))} className={`${inputCls} mt-0.5 w-full`} />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button type="button" onClick={() => {
                let params: Record<string, any> = {}
                try { params = JSON.parse(reporterDraft.params || '{}') } catch { return }
                onSaveChannel({ platform_id: null, channel: 'reporter', enabled: true, recipients: [], params })
              }} className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border-success/40 text-success hover:bg-success-surface`}>Save</button>
              {reporterRow?.updated_by && <span className={`font-mono text-[10px] ${muted}`}>by {reporterRow.updated_by} · {fmtDate(reporterRow.updated_at)}</span>}
            </div>
          </div>
        )}
      </div>

      <ProvidersPanel theme={theme} role="registrar" rows={config.registrars} checks={config.form_checks} onSave={onSaveRegistrar} onDelete={onDeleteRegistrar} />
      <ProvidersPanel theme={theme} role="hoster" rows={config.hosters} checks={config.form_checks} onSave={onSaveHoster} onDelete={onDeleteHoster} />
    </div>
  )
}

// Providers → workers, one table per role. Sorted by the domains at stake;
// a row with neither an address nor a form is inventory the data wrote and
// nobody has mapped yet. Each row edits in place: the email worker's
// address, the form worker's URL, module and status, the indicative captcha
// kinds. A form whose latest drift check disagrees with what was verified is
// flagged (takedown_form_check).
function ProvidersPanel({ theme, role, rows, checks, onSave, onDelete }: {
  theme: ProcessedTheme
  role: 'registrar' | 'hoster'
  rows: ProviderContact[]
  checks: FormCheck[]
  onSave: (row: Partial<ProviderContact>) => Promise<void>
  onDelete: (row: ProviderContact) => Promise<void>
}) {
  const { muted, border, text } = baseDashboardClasses(theme)
  const inputCls = `rounded border px-2 py-1 font-mono text-[11px] ${border} bg-card text-foreground`
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState<Partial<ProviderContact>>({})
  const [adding, setAdding] = useState<Partial<ProviderContact>>({ priority: 100, form_status: 'unmapped' })
  const [showMapped, setShowMapped] = useState(true)

  const sorted = useMemo(() => [...rows].sort((a, b) => (b.domains_seen ?? 0) - (a.domains_seen ?? 0) || providerKey(a).localeCompare(providerKey(b))), [rows])
  const unmapped = sorted.filter(isUnmappedProvider)
  const domainsAtStake = unmapped.reduce((n, r) => n + (r.domains_seen ?? 0), 0)
  const checkFor = (r: ProviderContact) => checks.find((c) => c.role === role && r.provider && c.provider === r.provider)
  const kindSelect = (value: string | null | undefined, onChange: (v: string | null) => void, title: string) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} className={`${inputCls} w-40`} title={title}>
      <option value="">{title}: —</option>
      {CAPTCHA_KINDS.map((k) => <option key={k} value={k}>{captchaLabel(k)}</option>)}
    </select>
  )
  const keyField = (d: Partial<ProviderContact>, set: (p: Partial<ProviderContact>) => void) => role === 'registrar' ? (
    <>
      <input value={d.registrar_pattern ?? ''} onChange={(e) => set({ registrar_pattern: e.target.value })} placeholder="%registrar name%" className={`${inputCls} w-40`} title="ILIKE pattern on the WHOIS registrar" />
      <input value={d.tld_pattern ?? ''} onChange={(e) => set({ tld_pattern: e.target.value })} placeholder=".tld" className={`${inputCls} w-16`} title="A ccTLD registry: matches names whose WHOIS publishes no registrar" />
    </>
  ) : (
    <>
      <input value={d.abuse_domain ?? ''} onChange={(e) => set({ abuse_domain: e.target.value })} placeholder="abuse mailbox domain" className={`${inputCls} w-40`} title="The domain of the network's RDAP abuse mailbox (amazonaws.com)" />
      <input value={d.provider_pattern ?? ''} onChange={(e) => set({ provider_pattern: e.target.value })} placeholder="%OWNER NAME%" className={`${inputCls} w-36`} title="Only when the mailbox varies: a pattern on the RDAP owner name" />
    </>
  )
  const workerFields = (d: Partial<ProviderContact>, set: (p: Partial<ProviderContact>) => void) => (
    <>
      <input value={d.provider ?? ''} onChange={(e) => set({ provider: e.target.value })} placeholder="display name" className={`${inputCls} w-36`} />
      <input value={d.abuse_email ?? ''} onChange={(e) => set({ abuse_email: e.target.value })} placeholder="abuse@… (email worker)" className={`${inputCls} w-52`} />
      <input value={d.form_url ?? ''} onChange={(e) => set({ form_url: e.target.value })} placeholder="form URL (form worker)" className={`${inputCls} w-64`} />
      <input value={d.worker_impl ?? ''} onChange={(e) => set({ worker_impl: e.target.value })} placeholder="worker module (dynadot)" className={`${inputCls} w-36`} title="libs/tooling/takedown/workers/<module>.py; 'generic' guesses the fields and only dry-runs" />
      <select value={d.form_status ?? 'unmapped'} onChange={(e) => set({ form_status: e.target.value as FormStatus })} className={`${inputCls} w-36`} title="unmapped: no worker · mapped: dry run only · verified: submits">
        {(['unmapped', 'mapped', 'verified'] as FormStatus[]).map((s) => <option key={s} value={s}>{FORM_STATUS_LABEL[s]}</option>)}
      </select>
      {kindSelect(d.gate_kind, (v) => set({ gate_kind: v }), 'gate')}
      {kindSelect(d.captcha_kind, (v) => set({ captcha_kind: v }), 'captcha')}
    </>
  )
  const clean = (d: Partial<ProviderContact>): Partial<ProviderContact> => ({
    ...d, abuse_email: d.abuse_email || null, form_url: d.form_url || null, worker_impl: d.worker_impl || null, provider: d.provider || null,
    tld_pattern: d.tld_pattern || null, abuse_domain: d.abuse_domain || null, provider_pattern: d.provider_pattern || null,
  })

  return (
    <div className="flex flex-col gap-1" data-providers={role}>
      <div className={`mt-2 flex flex-wrap items-center gap-2 font-mono text-[9px] uppercase tracking-widest ${muted}`}>
        <ShieldCheck className="inline h-3 w-3" /> {role === 'registrar' ? 'Registrars → workers' : 'Hosting providers → workers'}
        <span className={text}>{rows.length}</span>
        {unmapped.length > 0 && <Chip label={`${unmapped.length} without a worker · ${domainsAtStake} domain${domainsAtStake === 1 ? '' : 's'} at stake`} tone="amber" title="Seen in the data; neither an address nor a form yet" />}
        <span className="flex-1" />
        <label className="inline-flex items-center gap-1 normal-case"><input type="checkbox" checked={showMapped} onChange={(e) => setShowMapped(e.target.checked)} /> show mapped</label>
        <span className="normal-case">{role === 'registrar' ? 'matched on the WHOIS registrar (name, then pattern, then TLD); WHOIS mailbox wins unless "override"' : 'matched on the RDAP abuse mailbox domain, then the owner name'}</span>
      </div>
      {sorted.filter((r) => showMapped || isUnmappedProvider(r)).map((r) => {
        const chk = checkFor(r)
        const drift = chk && chk.agrees === 'CHECK'
        const isEditing = editing === r.id
        return (
          <div key={r.id} className={`flex flex-wrap items-center gap-2 rounded border px-3 py-1.5 ${border} ${isUnmappedProvider(r) ? 'border-warning-border bg-warning-surface/30' : ''}`} data-provider-row={providerKey(r)}>
            {isEditing ? (
              <>
                {keyField(draft, (p) => setDraft({ ...draft, ...p }))}
                {workerFields(draft, (p) => setDraft({ ...draft, ...p }))}
                <button type="button" onClick={() => { onSave(clean({ ...draft, id: r.id })); setEditing(null) }} className="rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border-success/40 text-success hover:bg-success-surface">Save</button>
                <button type="button" onClick={() => setEditing(null)} className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${border} ${muted}`}>Back</button>
              </>
            ) : (
              <>
                <span className={`font-mono text-[11px] ${text}`} title={providerKey(r)}>{r.provider ?? providerKey(r)}</span>
                <span className={`font-mono text-[10px] ${muted}`}>{providerKey(r)}{r.tld_pattern ? ` · ${r.tld_pattern}` : ''}</span>
                {r.domains_seen != null && <Chip label={`${r.domains_seen} domain${r.domains_seen === 1 ? '' : 's'}`} tone="neutral" title={r.last_seen_at ? `last seen ${fmtDate(r.last_seen_at)}` : ''} />}
                <span className={`font-mono text-[11px] ${r.abuse_email ? muted : 'text-warning'}`}>{r.abuse_email ?? 'no email'}</span>
                {r.form_url ? (
                  <>
                    <a href={r.form_url} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-signal hover:underline">form</a>
                    <Chip label={FORM_STATUS_LABEL[r.form_status ?? 'unmapped']} tone={FORM_STATUS_TONE[r.form_status ?? 'unmapped']} title={r.worker_impl ? `worker module ${r.worker_impl}${r.form_verified_on ? ` · verified ${r.form_verified_on}${r.form_verified_by ? ` by ${r.form_verified_by}` : ''}` : ''}` : 'no worker module yet'} />
                    {r.gate_kind && r.gate_kind !== 'none' && <Chip label={`gate: ${captchaLabel(r.gate_kind)}`} tone="amber" />}
                    {r.captcha_kind && <Chip label={captchaLabel(r.captcha_kind)} tone="neutral" title="indicative" />}
                    {drift && <Chip label="form changed?" tone="red" title={`latest check saw ${chk?.captcha_kind_detected ?? '?'}, verified ${chk?.captcha_kind_verified ?? '?'} (${fmtDate(chk?.checked_at)})`} />}
                  </>
                ) : (
                  <span className={`font-mono text-[10px] ${muted}`}>no form</span>
                )}
                {r.override_whois && <Chip label="override" tone="amber" title="The table's address beats the WHOIS one" />}
                {r.notes && <span className={`font-mono text-[10px] ${muted}`} title={r.notes}>{r.notes.slice(0, 60)}</span>}
                <span className="flex-1" />
                {role === 'registrar' && r.abuse_email && (
                  <button type="button" onClick={() => onSave({ id: r.id, registrar_pattern: r.registrar_pattern, override_whois: !r.override_whois })} className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-warning hover:border-warning-border`}>{r.override_whois ? 'prefer whois' : 'override whois'}</button>
                )}
                <button type="button" onClick={() => { setEditing(r.id); setDraft({ ...r }) }} className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-signal hover:border-signal`}>edit</button>
                <button type="button" onClick={() => onDelete(r)} className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-destructive hover:border-destructive`}>delete</button>
              </>
            )}
          </div>
        )
      })}
      <div className={`flex flex-wrap items-center gap-2 rounded border px-3 py-1.5 ${border}`}>
        {keyField(adding, (p) => setAdding({ ...adding, ...p }))}
        {workerFields(adding, (p) => setAdding({ ...adding, ...p }))}
        <button type="button" disabled={role === 'registrar' ? !(adding.registrar_pattern ?? '').trim() : !((adding.abuse_domain ?? '').trim() || (adding.provider_pattern ?? '').trim())}
          onClick={() => { onSave(clean(adding)); setAdding({ priority: 100, form_status: 'unmapped' }) }}
          className="rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border-success/40 text-success hover:bg-success-surface disabled:opacity-40">Add</button>
      </div>
    </div>
  )
}
